import { unstable_cache } from 'next/cache';

import { HttpService } from '@bytel/leviator';
import { getTrackerId } from '@bytel/logger-nextjs/server';

import { getAppConfig } from '@services/config';

import { BytelAxiosRequestConfig, Callback } from '@app-types/axios';
import { UserType } from '@app-types/user';

import { isServer } from '@helpers/config';
import { getClientCookies } from '@helpers/cookies-client';

import { API_X_SOURCE, SAPI_X_VERSION } from '@constants/api';

import { AxiosInstance, AxiosResponse } from 'axios';
import { createHash } from 'crypto';
import { v4 as uuid } from 'uuid';

import { HttpConfig } from './http';

const appConfig = getAppConfig();

export class AxiosService implements HttpService<HttpConfig> {
    private readonly isApi: boolean;
    private readonly isCms: boolean;
    private readonly defaultApiVersion: string;
    private httpInstance: AxiosInstance;
    private headers: Record<string, string> = {};

    constructor(
        axiosInstance: AxiosInstance,
        isApi: boolean = false,
        isCms: boolean = false,
        defaultApiVersion: string = SAPI_X_VERSION.V4,
    ) {
        this.httpInstance = axiosInstance;
        this.isApi = isApi;
        this.isCms = isCms;
        this.defaultApiVersion = defaultApiVersion;
    }

    public setInitialHeaders(headers: Headers): void {
        this.headers = Object.fromEntries(headers.entries());
    }

    public async get<Response, Body = object>(
        url: string,
        data?: Body,
        httpConfig: HttpConfig = {},
    ): Promise<Response> {
        const config = await this.initConfig(httpConfig);
        const cacheKey = this.generateCacheKey(url, data, httpConfig.user);

        config.params = data;

        return await this.launchRequest(
            async () => {
                return this.httpInstance
                    .get<Response, AxiosResponse<Response>, Body>(url, config)
                    .then((response) => this.processResponse(response));
            },
            cacheKey,
            config,
        )();
    }

    public async post<Response, Body = object>(url: string, data?: Body, httpConfig: HttpConfig = {}) {
        const config = await this.initConfig(httpConfig);
        const cacheKey = this.generateCacheKey(url, data, httpConfig.user);

        return await this.launchRequest(
            async () => {
                return this.httpInstance
                    .post<Response, AxiosResponse<Response>, Body>(url, data, config)
                    .then((response) => this.processResponse(response));
            },
            cacheKey,
            config,
        )();
    }

    public async put<Response, Body = Record<string, unknown>>(url: string, data?: Body, httpConfig: HttpConfig = {}) {
        const config = await this.initConfig(httpConfig);
        const cacheKey = this.generateCacheKey(url, data, httpConfig.user);

        return await this.launchRequest(
            async () => {
                return this.httpInstance
                    .put<Response, AxiosResponse<Response>, Body>(url, data, config)
                    .then((response) => this.processResponse(response));
            },
            cacheKey,
            config,
        )();
    }

    public async delete<Response>(url: string, httpConfig: HttpConfig = {}) {
        const config = await this.initConfig(httpConfig);

        return await this.httpInstance.delete<Response>(url, config).then((response) => this.processResponse(response));
    }

    private launchRequest<T extends Callback>(cb: T, key: string, config: BytelAxiosRequestConfig): T {
        if (!config.next?.revalidate || typeof window !== 'undefined') {
            return cb;
        }

        return unstable_cache(cb, [key], { tags: config?.next?.tags, revalidate: config?.next?.revalidate });
    }

    protected processResponse<T>(response: AxiosResponse<T>) {
        return response.data;
    }

    protected async initConfig(httpConfig: HttpConfig = {}): Promise<BytelAxiosRequestConfig> {
        let headersObject = httpConfig.headers ?? {};

        const config: BytelAxiosRequestConfig = {
            signal: httpConfig?.controller ? httpConfig.controller.signal : undefined,
            ...(httpConfig.timeout !== undefined && { timeout: httpConfig.timeout }),
        };

        if (httpConfig.user) {
            config.user = {
                idPersonne: httpConfig.user.sub,
                login: httpConfig.user.login,
                userType: httpConfig.user.user_type,
            };
        }

        if (httpConfig.ttl !== undefined) {
            config.next = {
                ...config.next,
                revalidate: httpConfig.ttl * 60,
            };
        }

        if (httpConfig?.tags && httpConfig.tags.length > 0) {
            config.next = {
                ...config.next,
                tags: httpConfig.tags,
            };
        }

        if (!this.isCms) {
            if (!headersObject['content-type']) {
                headersObject['content-type'] = 'application/json';
            }
        } else {
            config.next = {
                ...config.next,
                mute: true,
            };
        }

        headersObject = await this.setHeadersApi(httpConfig, headersObject);
        headersObject = this.cleanHeaders(headersObject);

        return { ...config, headers: headersObject };
    }

    private async setHeadersApi(
        httpConfig: HttpConfig,
        baseHeaders: Record<string, string>,
    ): Promise<Record<string, string>> {
        const headers = { ...baseHeaders };

        headers['x-source'] = API_X_SOURCE;
        headers['x-request-id'] = uuid();

        const trackerId = isServer() ? await this.getTrackerIdSafe() : getClientCookies('bytel_logger_trackerId');
        if (trackerId) {
            headers['trackerId'] = trackerId;
        }

        if (this.isApi) {
            headers['x-version'] ??= this.defaultApiVersion;
            headers['accept-encoding'] = 'gzip';

            const clientIp = this.headers['x-forwarded-for'];
            if (clientIp) {
                headers['x-real-ip'] = clientIp;
                headers['x-bytel-ip'] = clientIp;
            }

            if (appConfig.bench && !headers['x-banc']) {
                headers['x-banc'] = appConfig.bench;
            }

            if (httpConfig.user) {
                headers['authorization'] = `Bearer ${httpConfig.user.accessToken}`;
            }
        }

        return headers;
    }

    private cleanHeaders(headers: Record<string, string>): Record<string, string> {
        delete headers['host'];
        delete headers['accept'];
        delete headers['accept-language'];
        delete headers['connection'];
        delete headers['content-length'];
        delete headers['origin'];
        return headers;
    }

    private async getTrackerIdSafe(): Promise<string> {
        try {
            return await getTrackerId();
        } catch {
            return '';
        }
    }

    private generateCacheKey<Body = object>(url: string, data?: Body, user?: UserType): string {
        const keyParts = [url];
        if (data && Object.keys(data).length > 0) {
            keyParts.push(JSON.stringify(data));
        }
        if (user?.login) {
            keyParts.push(JSON.stringify(user.login));
        }
        const rawKey = keyParts.join('-');

        return createHash('sha256').update(rawKey).digest('hex');
    }
}
